import Checkbox from "../../../../components/FormElements/Checkbox";
import {useTranslation} from "react-i18next";
import React, {useEffect, useState} from "react";
import styles from "./styles.module.scss";
import {useSelector} from "react-redux";
import ButtonFilled from "../../../../components/FormElements/ButtonFilled";
import {format, addWeeks, addMonths, addYears} from "date-fns";
import branchApi from "../../../../store/branchStore/branchApi";
import {useFormik} from "formik";
import TextField from "../../../../components/FormElements/TextField";
import { store } from 'react-notifications-component';
import CascadeInfoBanner from "../../../../components/CascadeInfoBanner";
import { deriveChannelsView, isChannelConnected } from "../../../WhatsApp/channelHelpers";

const NOTIFICATIONS_TYPE = {
  "TEXT": "TEXT",
  "BONUS_TEXT": "BONUS_TEXT",
  "BONUS": "BONUS",
}

const BONUS_EXPIRY_OPTIONS = [
  { value: 6.048e+8, labelKey: 'week' }, // 1 week in milliseconds
  { value: 2.628e+9, labelKey: 'month' }, // 1 month in milliseconds
  { value: 7.884e+9, labelKey: 'NSBonus.three' }, // 3 months in milliseconds
  { value: 1.577e+10, labelKey: 'NSBonus.halfYear' }, // 6 months in milliseconds
  { value: 3.154e+10, labelKey: 'year' } // 1 year in milliseconds
];

const INITIAL_VALUES = {
  bonus_amount: 0,
  bonus_expiry_option: 2.628e+9, // Default to 1 week in milliseconds
  custom_message: "",
  exclude_overlapping: true,
  ignore_warnings: true,
  tags: []
}

const TagsNotifications = () => {
  const { t } = useTranslation()
  const {info} = useSelector((state) => state.branchReducer)
  const brandedWabaConnected = isChannelConnected(deriveChannelsView(info).channel_1);
  const [historyOfNotifications, setHistoryOfNotifications] = useState([])
  const [totalCount, setTotalCount] = useState([])
  const [notifcationsType, setNotifcationsType] = useState(NOTIFICATIONS_TYPE.TEXT)
  const [disableAutonotificationStatus, setDisableAutonotificationStatus] = useState(false);
  const [cascadeMailingExpiry, setCascadeMailingExpiry] = useState(null);

  useEffect(() => {
      if (info?.configuration?.private_cashback?.is_cascade) {
          branchApi.getBalanceExpiration(info.id)
              .then(res => {
                  if (res?.data?.mailing !== undefined) {
                      setCascadeMailingExpiry(res.data.mailing);
                  }
              })
              .catch(() => {});
      }
  }, [info]);

  const onDisableAutoNotification = () => {
    if (!info) return

    setDisableAutonotificationStatus(true);
    branchApi.branchesCustomersUploadedStop(info.group_id, {
      branch_ids: [info.id]
    })
      .then((res) => {
        store.addNotification({
          title: t('success'),
          message: 'Рассылка успешно остановлена',
          type: "success",
          insert: "top",
          container: "top-right",
          animationIn: ["animate__animated", "animate__fadeIn"],
          animationOut: ["animate__animated", "animate__fadeOut"],
          dismiss: {
            duration: 5000,
            onScreen: true
          }
        });
      })
      .finally(() => {
        setDisableAutonotificationStatus(false);
      });
  }

  const formik = useFormik({
    initialValues: {...INITIAL_VALUES},
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true)
        let body = {
          exclude_overlapping: values.exclude_overlapping,
          ignore_warnings: values.ignore_warnings,
          tags: values.tags,
        }
        
        // Calculate bonus expiry timestamp if needed
        const getBonusExpiry = () => {
          // Get current time in Almaty timezone (UTC+5)
          const currentTime = new Date();
          const almatyOffset = 5 * 60 * 60 * 1000; // 5 hours in milliseconds
          const utcTime = currentTime.getTime() + (currentTime.getTimezoneOffset() * 60 * 1000);
          const almatyTime = utcTime + almatyOffset;
          return almatyTime + values.bonus_expiry_option;
        };
        
        switch (notifcationsType) {
          case NOTIFICATIONS_TYPE.TEXT:
            body = {
              ...body,
              notification_type: 'custom',
              custom_message: values.custom_message,
            }
            break
          case NOTIFICATIONS_TYPE.BONUS_TEXT:
            body = {
              ...body,
              notification_type: 'custom',
              custom_message: values.custom_message,
              bonus_amount: +values.bonus_amount,
              bonus_expiry: getBonusExpiry()
            }
            break
          case NOTIFICATIONS_TYPE.BONUS:
            body = {
              ...body,
              bonus_amount: +values.bonus_amount,
              bonus_expiry: getBonusExpiry()
            }
            break
        }
        const response = branchApi.postBranchTagCustomersActions(info.id, body)
        const campaingsResponse = branchApi.getTagsAnalyticsCampaigns(info.id)

        setHistoryOfNotifications(campaingsResponse?.data?.campaigns || [])
        setTotalCount(campaingsResponse?.data?.total_count || 0)

        store.addNotification({
          title: '',
          message: t('successMessageSent'),
          type: "success",
          insert: "top",
          container: "top-right",
          animationIn: ["animate__animated", "animate__fadeIn"],
          animationOut: ["animate__animated", "animate__fadeOut"],
          dismiss: {
            duration: 5000,
            onScreen: true
          }
        });
      } finally {
        setSubmitting(false)
      }
    }
  })

  // Get default expiry interval from configuration
  const getDefaultExpiryInterval = () => {
    return info?.configuration?.private_cashback?.expire_interval || 6.048e+8; // Default to 1 week if no config
  };

  useEffect(() => {
    formik.setFieldValue('custom_message', '')
    formik.setFieldValue('bonus_amount', '')
    formik.setFieldValue('bonus_expiry_option', getDefaultExpiryInterval()) // Reset to config default
  }, [notifcationsType, info])

  useEffect(() => {
    let intervalId = null
    if (info?.id) {
      branchApi.getTagsAnalyticsCampaigns(info.id)
        .then((response) => {
          setHistoryOfNotifications(response?.data?.campaigns || [])
          setTotalCount(response?.data?.total_count || 0)
        })

      intervalId = setInterval(() => {
        branchApi.getTagsAnalyticsCampaigns(info.id)
          .then((response) => {
            setHistoryOfNotifications(response?.data?.campaigns || [])
            setTotalCount(response?.data?.total_count || 0)
          })
      }, 10000)
    }

    return () => {
      intervalId && clearInterval(intervalId)
    }
  }, [info?.id]);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {brandedWabaConnected && (
        <div
          style={{
            backgroundColor: '#fff8e1',
            border: '1px solid #ffe2a8',
            color: '#8a6d3b',
            padding: '12px 16px',
            borderRadius: 10,
            marginBottom: 0,
          }}
        >
          {t('NSCampaigns.legacyWabaWarning')}{' '}
          <a href="/campaigns" style={{ color: '#8a6d3b', fontWeight: 600 }}>
            {t('NSSidebar.campaigns')}
          </a>
        </div>
      )}
      {info?.configuration?.private_cashback?.is_cascade && (
          <div style={{ maxWidth: 600 }}>
              <CascadeInfoBanner
                  sourceLabel={t('NSBonusMode.sourceMailing')}
                  expirationValue={cascadeMailingExpiry}
              />
          </div>
      )}
      <form onSubmit={formik.handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
        <div style={{ display: "flex", flexDirection: 'column', gap: 16 }}>
          <Checkbox
            onChange={() => setNotifcationsType(NOTIFICATIONS_TYPE.TEXT)}
            checked={notifcationsType === NOTIFICATIONS_TYPE.TEXT}
            label={t('NSTag.textMailing')}
          />
          <Checkbox
            onChange={() => setNotifcationsType(NOTIFICATIONS_TYPE.BONUS_TEXT)}
            checked={notifcationsType === NOTIFICATIONS_TYPE.BONUS_TEXT}
            label={t('NSTag.textBonusMailing')}
          />
          <Checkbox
            onChange={() => setNotifcationsType(NOTIFICATIONS_TYPE.BONUS)}
            checked={notifcationsType === NOTIFICATIONS_TYPE.BONUS}
            label={t('NSTag.bonusWithoutTextMailing')}
          />
        </div>

        {notifcationsType === NOTIFICATIONS_TYPE.TEXT || notifcationsType === NOTIFICATIONS_TYPE.BONUS_TEXT ? (
          <textarea
            className={styles.textarea}
            required
            placeholder={t('Form.requiredField')}
            value={formik.values.custom_message}
            onChange={(e) => formik.setFieldValue('custom_message', e.target.value)}
          />
        ) : null}

        <div className={styles.tags}>
          {info?.configuration?.tags?.map((tag) => (
            <div
              key={tag.id}
              className={formik.values.tags.includes(tag.id) ? styles.active : ''}
              onClick={() => {
              if (!formik.values.tags.includes(tag.id)) {
                formik.setFieldValue('tags', [...formik.values.tags, tag.id])
              } else {
                formik.setFieldValue('tags', formik.values.tags.filter(tagId => tagId !== tag.id))
              }
            }}>
              {tag.name}
            </div>
          ))}
        </div>

        {notifcationsType === NOTIFICATIONS_TYPE.BONUS || notifcationsType === NOTIFICATIONS_TYPE.BONUS_TEXT ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <p style={{ margin: 0 }}>{t('NSTag.giftBonusCount')}:</p>
              <TextField
                width={100}
                type="number"
                value={formik.values.bonus_amount}
                onChange={(e) => formik.setFieldValue('bonus_amount', e.target.value)}
                required
              />
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <p style={{ margin: 0 }}>{t('NSTag.bonusExpiry')}:</p>
              <select
                value={formik.values.bonus_expiry_option}
                onChange={(e) => formik.setFieldValue('bonus_expiry_option', Number(e.target.value))}
                style={{
                  padding: '8px 12px',
                  borderRadius: '4px',
                  border: '1px solid #97D6B9',
                  backgroundColor: 'white',
                  fontSize: '14px'
                }}
              >
                {BONUS_EXPIRY_OPTIONS.map(option => {
                  // Check if this option matches the configuration default and mark it
                  const isDefault = option.value === getDefaultExpiryInterval();
                  const label = t(option.labelKey) + (isDefault ? ` (${t('NSTag.default')})` : '');
                  return (
                    <option key={option.value} value={option.value}>
                      {label}
                    </option>
                  );
                })}
              </select>
            </div>
            <div style={{ 
              backgroundColor: '#FFF3CD', 
              border: '1px solid #FFEAA7',
              borderRadius: '4px',
              padding: '12px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '14px',
              color: '#856404'
            }}>
              <span style={{ fontSize: '16px' }}>⚠️</span>
              <span>{t('NSTag.bonusExpiryWarning')}</span>
            </div>
          </div>
        ) : null}

        <div style={{ display: 'flex', gap: 16 }}>
          <ButtonFilled variant="red" type="button" disabled={formik.isSubmitting} onClick={onDisableAutoNotification} loading={disableAutonotificationStatus}>
            {t('stopPreviousMailing')}
          </ButtonFilled>
          <ButtonFilled variant="green" type="submit" loading={formik.isSubmitting}>
            {t('start')}
          </ButtonFilled>
        </div>
      </form>

      {historyOfNotifications.length ? (
        <div style={{ display: "flex", alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 500 }}>{t('history')}</h2>
          <p style={{ margin: 0 }}>{t('totalCount')}: {totalCount}</p>
        </div>
      ) : null}

      {historyOfNotifications.length ? (
        <div className={styles.historyOfNotifications}>
          {historyOfNotifications.map((historyItem) => (
            <div className={styles.historyItem} key={historyItem.id}>
              <div className={styles.historyTop}>
                <div>{format(new Date(historyItem.created_at), 'dd.MM.yyyy')}</div>
              </div>
              <div className={styles.historyCenter}>
                {historyItem.message && <span>{t('message')}: {historyItem.message}</span>}
                {historyItem.bonus_amount && <span>{t('NSAssessment.bonuses')}: {historyItem.bonus_amount}</span>}
                {historyItem.bonuses_accrued && <span>{t('NSGeneral.bonuses')}: {historyItem.bonuses_accrued}</span>}
                {historyItem.tags?.length && <span>{t('NSTag.tags')}: {historyItem.tags.map(tag => tag.title).join(',    ')}</span>}
              </div>
              <div className={styles.historyBottom}>
                <div>{historyItem.notifications_sent}</div>
                {/*{historyItem.completed_date ? (*/}
                {/*  <div>{format(new Date(historyItem.completed_date), 'dd.MM.yyyy')}</div>*/}
                {/*) : null}*/}
              </div>
            </div>
          ))}
        </div>
      ) : null}

    </div>
  )
}

export default TagsNotifications